import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Party } from "@/lib/types";
import { listParties } from "@/features/parties/parties.api";
import { focusNextAfterChoice, nextFocusable } from "@/lib/focusNext";

// Who gives the order: a relation with the customer role, found on name or VR number.
// Once chosen it is a line to read with "Andere klant" beside it, not a field that keeps
// asking. Its own file because the entry screen and the take-over screen both use it.
// The search name from AFAS goes in brackets behind the name: a customer has several relations
// with one name (FrieslandCampina Nederland B.V. four times) and the search name tells them apart.

function nameWithSearchName(party: Party): string {
  const search = party.shortName?.trim();
  return search && search !== party.name ? `${party.name} (${search})` : party.name;
}

export function CustomerPicker({ id, value, onChange }: { id: string; value: Party | null; onChange: (party: Party | null) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [items, setItems] = useState<Party[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const latest = useRef(0);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setItems([]);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      // Only a customer somebody approved: one from AFAS that waits is used for nothing (S54).
      listParties({ q, role: "customer", isActive: "true", "filter.approval": "approved" })
        .then((page) => {
          if (request !== latest.current) return;
          setItems(page.data.slice(0, 8));
          setActive(-1);
          setOpen(true);
        })
        .catch(() => {
          if (request === latest.current) setItems([]);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  if (value) {
    const debtor = value.roles?.find((role) => role.role === "customer")?.number;
    return (
      <div
        className="wl-hstack"
        style={{
          gap: "8px",
          justifyContent: "space-between",
          minHeight: "var(--control-height-md)",
        }}
      >
        <div className="t-body-sm">
          <span style={{ fontWeight: "var(--fw-medium)" }}>{nameWithSearchName(value)}</span>{" "}
          <span className="t-mono t-caption t-muted">{debtor ?? value.code}</span>
        </div>
        <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(null)}>
          {t("orders.customerChange")}
        </button>
      </div>
    );
  }

  const listId = `${id}-list`;
  return (
    <div ref={wrapper} style={{ position: "relative" }}>
      <input
        id={id}
        className="wl-input"
        type="search"
        role="combobox"
        autoComplete="off"
        aria-expanded={open && text.trim().length >= 2}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        placeholder={t("orders.customerSearchPlaceholder")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open || items.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((index) => Math.min(index + 1, items.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if ((e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) && active >= 0) {
            const item = items[active];
            if (!item) return;
            e.preventDefault();
            // Tab takes the highlighted customer too, and then goes on to the next field
            // (Camiel, 21-09-2026: arrowing onto a suggestion and pressing Tab left it empty).
            const next = e.key === "Tab" ? nextFocusable(wrapper.current) : null;
            onChange(item);
            focusNextAfterChoice(next);
          }
        }}
      />
      {open && text.trim().length >= 2 && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: "var(--z-overlay)",
            margin: "4px 0 0",
            padding: "4px",
            listStyle: "none",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          {items.length === 0 && (
            <li className="t-caption t-muted" style={{ padding: "6px 8px" }}>
              {t("orders.customerNothingFound")}
            </li>
          )}
          {items.map((item, index) => (
            <li
              key={item.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className="t-body-sm"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(item);
              }}
              onMouseEnter={() => setActive(index)}
              style={{
                padding: "6px 8px",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                background: index === active ? "var(--surface-hover)" : undefined,
              }}
            >
              {nameWithSearchName(item)}{" "}
              <span className="t-mono t-caption t-muted">{item.roles?.find((role) => role.role === "customer")?.number ?? item.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
